import React, { useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  ChevronDown,
  Pencil,
  Plus,
  Power,
  Search,
  Trash2,
  UploadCloud,
  X,
} from "lucide-react";
import { restaurantAdminService, rowsOf } from "../../services/restaurantAdmin.service";
import { appConfirm } from "../../utils/appPopup";
import { isBase64Image, resolveRestaurantFoodImageUrl } from "../../utils/restaurantFoodImageUrl";

function Input({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={
        "h-[44px] w-full rounded-[10px] border border-[#f2c6cc] bg-white px-4 text-[12px] text-slate-600 outline-none transition placeholder:text-slate-400 focus:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db] " +
        className
      }
    />
  );
}

function Button({ children, variant = "primary", className = "", ...props }) {
  const cls =
    variant === "primary"
      ? "bg-[#d86a78] text-white hover:bg-[#c95766]"
      : "border border-[#f2c6cc] bg-white text-[#d86a78] hover:bg-[#fff6f7]";

  return (
    <button
      type="button"
      {...props}
      className={`inline-flex h-[40px] items-center justify-center gap-2 rounded-[10px] px-4 text-[12px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${cls} ${className}`}
    >
      {children}
    </button>
  );
}

function IconButton({ children, title, danger = false, className = "", ...props }) {
  const cls = danger
    ? "border-rose-200 text-rose-600 hover:bg-rose-50"
    : "border-[#f2c6cc] text-[#d86a78] hover:bg-[#fff6f7]";

  return (
    <button
      type="button"
      aria-label={title}
      title={title}
      {...props}
      className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] border bg-white transition disabled:cursor-not-allowed disabled:opacity-50 ${cls} ${className}`}
    >
      {children}
    </button>
  );
}

function SelectLike({ value, onChange, placeholder, options = [], disabled = false }) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => String(o.value) === String(value));

  return (
    <div className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
        className={`h-[44px] w-full rounded-[10px] border px-4 pr-10 text-left text-[12px] outline-none transition ${
          disabled
            ? "cursor-not-allowed border-[#f2c6cc] bg-slate-50 text-slate-400"
            : "border-[#f2c6cc] bg-white text-slate-600 hover:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db]"
        }`}
      >
        {selected?.label || placeholder}
      </button>

      <ChevronDown
        size={16}
        className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition ${open ? "rotate-180" : ""}`}
      />

      {open && !disabled ? (
        <div className="absolute z-[110] mt-2 max-h-60 w-full overflow-y-auto rounded-xl border border-[#f2c6cc] bg-white shadow-xl">
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              onChange("");
              setOpen(false);
            }}
            className="w-full px-4 py-3 text-left text-[12px] text-slate-500 hover:bg-[#fff4f6] hover:text-[#d86a78]"
          >
            {placeholder}
          </button>

          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
              className={`w-full px-4 py-3 text-left text-[12px] transition ${
                String(value) === String(o.value)
                  ? "bg-[#d86a78] text-white"
                  : "text-slate-600 hover:bg-[#fff4f6] hover:text-[#d86a78]"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function normalizeId(row) {
  return row?.id || row?.categoryId || row?.category_id || row?.subcategoryId || row?.subcategory_id || row?.tagId || row?.tag_id || "";
}

function normalizeName(row) {
  return row?.name || row?.categoryName || row?.category_name || row?.subcategoryName || row?.subcategory_name || row?.tagName || row?.tag_name || "Unnamed";
}

function categoryIdOf(row) {
  return row?.categoryId || row?.category_id || row?.parentId || row?.parent_id || "";
}

function imageOf(row) {
  const url = row?.imageUrl || row?.image_url || "";
  return isBase64Image(url) ? "" : url;
}

function isActiveOf(row) {
  return row?.isActive ?? row?.is_active ?? true;
}

const MAX_IMAGE_SIZE_MB = 3;
const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024;

async function fileToImage(file) {
  if (!file) return "";
  if (!String(file.type || "").startsWith("image/")) throw new Error("Only image files are allowed");
  if (file.size > MAX_IMAGE_SIZE_BYTES) throw new Error(`Image must be ${MAX_IMAGE_SIZE_MB} MB or less`);

  const res = await restaurantAdminService.uploadFoodImage(file);
  const url = res?.data?.imageUrl || res?.data?.url || res?.imageUrl || res?.url;
  if (!url) throw new Error("Upload failed: image URL missing");
  return url;
}

function SingleImageUpload({ label, value, onChange, required = false, onError }) {
  const inputId = React.useId();
  const previewSrc = resolveRestaurantFoodImageUrl(imageOf({ imageUrl: value }));
  const [uploading, setUploading] = useState(false);

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      const nextUrl = await fileToImage(file);
      if (nextUrl) onChange(nextUrl);
    } catch (error) {
      onError?.(error?.message || "Invalid image");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  return (
    <div className="rounded-[14px] border border-dashed border-[#f2c6cc] bg-[#fffafb] p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[12px] font-semibold text-slate-800">
            {label}
            {required ? " *" : ""}
          </div>
          <div className="text-[11px] text-slate-500">Upload 1 image • Max {MAX_IMAGE_SIZE_MB} MB</div>
        </div>

        <label
          htmlFor={inputId}
          className="inline-flex h-[36px] cursor-pointer items-center gap-2 rounded-[10px] border border-[#f2c6cc] bg-white px-4 text-[12px] font-semibold text-[#d86a78] hover:bg-[#fff6f7]"
        >
          <UploadCloud size={14} /> {uploading ? "Uploading..." : "Upload"}
        </label>
        <input id={inputId} type="file" accept="image/*" className="hidden" onChange={handleFile} />
      </div>

      {previewSrc ? (
        <div className="relative h-[92px] w-[92px] overflow-hidden rounded-[12px] border border-[#f2c6cc] bg-white">
          <img src={previewSrc} alt="preview" className="h-full w-full object-cover" />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-white/90 text-rose-600 shadow"
          >
            <Trash2 size={13} />
          </button>
        </div>
      ) : (
        <div className={`rounded-[12px] bg-white px-4 py-5 text-center text-[12px] ${required ? "font-bold text-rose-600" : "font-semibold text-slate-400"}`}>
          {required ? `${label} is required` : "No image selected"}
        </div>
      )}
    </div>
  );
}

function EntityImage({ item, label }) {
  const src = resolveRestaurantFoodImageUrl(imageOf(item));
  if (src) return <img src={src} alt={label || "catalog"} className="h-full w-full object-cover" />;
  return <span className="text-[10px] font-bold text-slate-400">{String(label || "?").slice(0, 1).toUpperCase()}</span>;
}

function ActiveBadge({ active }) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold ${active ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${active ? "bg-emerald-500" : "bg-rose-500"}`} />
      {active ? "Active" : "Inactive"}
    </span>
  );
}

const emptyNodeForm = {
  type: "main",
  name: "",
  imageUrl: "",
  parentId: "",
};

export default function FoodCatalogManagerDrawer({ open, onClose, onSaved, initialTab = "categories" }) {
  const [tab, setTab] = useState(initialTab || "categories");
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [nodeForm, setNodeForm] = useState(emptyNodeForm);
  const [editingMainId, setEditingMainId] = useState("");
  const [editingSubId, setEditingSubId] = useState("");
  const [tagForm, setTagForm] = useState({ name: "", tagType: "cuisine" });
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const categoryOptions = useMemo(() => {
    return [...categories]
      .map((cat) => ({ value: normalizeId(cat), label: normalizeName(cat) }))
      .filter((x) => x.value)
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [categories]);

  const selectedCategory = useMemo(() => {
    return categories.find((cat) => String(normalizeId(cat)) === String(selectedCategoryId)) || null;
  }, [categories, selectedCategoryId]);

  const filteredCategories = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return [...categories]
      .filter((cat) => !needle || normalizeName(cat).toLowerCase().includes(needle))
      .sort((a, b) => normalizeName(a).localeCompare(normalizeName(b)));
  }, [categories, search]);

  const selectedSubcategories = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return [...subcategories]
      .filter((sub) => !selectedCategoryId || String(categoryIdOf(sub)) === String(selectedCategoryId))
      .filter((sub) => !needle || normalizeName(sub).toLowerCase().includes(needle))
      .sort((a, b) => normalizeName(a).localeCompare(normalizeName(b)));
  }, [search, selectedCategoryId, subcategories]);

  const visibleTags = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return [...tags]
      .filter((tag) => {
        if (!needle) return true;
        return (
          normalizeName(tag).toLowerCase().includes(needle) ||
          String(tag?.tagType || tag?.tag_type || "").toLowerCase().includes(needle)
        );
      })
      .sort((a, b) => normalizeName(a).localeCompare(normalizeName(b)));
  }, [search, tags]);

  async function load() {
    try {
      setLoading(true);
      setError("");

      const [catRes, subRes, tagRes] = await Promise.all([
        restaurantAdminService.listFoodCategories({ includeInactive: true }),
        restaurantAdminService.listFoodSubcategories({ includeInactive: true }),
        restaurantAdminService.listFoodTags({ includeInactive: true }),
      ]);

      const nextCategories = rowsOf(catRes);
      const nextSubcategories = rowsOf(subRes);

      setCategories(nextCategories);
      setSubcategories(nextSubcategories);
      setTags(rowsOf(tagRes));

      const currentExists = nextCategories.some((cat) => String(normalizeId(cat)) === String(selectedCategoryId));
      if (!currentExists) {
        setSelectedCategoryId(normalizeId(nextCategories[0]) || "");
      }
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Unable to load food catalog");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!open) return;
    setTab(initialTab || "categories");
    setSearch("");
    setError("");
    setSuccess("");
    setNodeForm(emptyNodeForm);
    setEditingMainId("");
    setEditingSubId("");
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialTab]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = window.setTimeout(() => setSuccess(""), 2500);
    return () => window.clearTimeout(timer);
  }, [success]);

  function clearNodeEdit() {
    setEditingMainId("");
    setEditingSubId("");
  }

  function startAddMainCategory() {
    setTab("categories");
    setError("");
    setSuccess("");
    clearNodeEdit();
    setNodeForm({ ...emptyNodeForm, type: "main" });
  }

  function startAddSubcategory() {
    setTab("categories");
    setError("");
    setSuccess("");
    clearNodeEdit();
    setNodeForm({ ...emptyNodeForm, type: "sub", parentId: selectedCategoryId || "" });
  }

  function startEditCategory(cat) {
    const id = normalizeId(cat);
    setTab("categories");
    setError("");
    setSuccess("");
    setEditingMainId(id);
    setEditingSubId("");
    setSelectedCategoryId(id);
    setNodeForm({ type: "main", name: normalizeName(cat), imageUrl: imageOf(cat), parentId: "" });
  }

  function startEditSubcategory(sub) {
    const id = normalizeId(sub);
    const parentId = categoryIdOf(sub) || selectedCategoryId || "";
    setTab("categories");
    setError("");
    setSuccess("");
    setEditingMainId("");
    setEditingSubId(id);
    setSelectedCategoryId(parentId);
    setNodeForm({ type: "sub", name: normalizeName(sub), imageUrl: imageOf(sub), parentId });
  }

  function setNodeType(type) {
    setError("");
    clearNodeEdit();
    setNodeForm((prev) => ({
      ...emptyNodeForm,
      type,
      parentId: type === "sub" ? selectedCategoryId || prev.parentId || "" : "",
    }));
  }

  async function afterSaved(message) {
    setSuccess(message);
    await load();
    onSaved?.();
  }

  async function saveNode() {
    const name = nodeForm.name.trim();
    const isSub = nodeForm.type === "sub";
    const parentId = nodeForm.parentId || selectedCategoryId || "";

    if (!name) return setError(isSub ? "Subcategory name is required" : "Category name is required");
    if (isSub && !parentId) return setError("Select parent category first");
    if (!nodeForm.imageUrl) return setError(isSub ? "Subcategory image is required" : "Category image is required");

    const wasEditingMain = Boolean(editingMainId);
    const wasEditingSub = Boolean(editingSubId);

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      if (isSub) {
        const payload = { categoryId: parentId, name, imageUrl: nodeForm.imageUrl };
        if (editingSubId) {
          await restaurantAdminService.updateFoodSubcategory(editingSubId, payload);
        } else {
          await restaurantAdminService.createFoodSubcategory({ ...payload, isActive: true });
        }
        clearNodeEdit();
        setNodeForm({ ...emptyNodeForm, type: "sub", parentId });
        setSelectedCategoryId(parentId);
        await afterSaved(wasEditingSub ? "Subcategory updated" : "Subcategory added");
      } else {
        const payload = { name, imageUrl: nodeForm.imageUrl };
        if (editingMainId) {
          await restaurantAdminService.updateFoodCategory(editingMainId, payload);
          setSelectedCategoryId(editingMainId);
        } else {
          await restaurantAdminService.createFoodCategory({ ...payload, isActive: true });
        }
        clearNodeEdit();
        setNodeForm({ ...emptyNodeForm, type: "main" });
        await afterSaved(wasEditingMain ? "Category updated" : "Category added");
      }
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Catalog save failed");
    } finally {
      setSaving(false);
    }
  }

  async function toggleCategoryStatus(cat) {
    const id = normalizeId(cat);
    if (!id) return;
    const next = !isActiveOf(cat);

    try {
      setSaving(true);
      setError("");
      await restaurantAdminService.setFoodCategoryStatus(id, next);
      await afterSaved(next ? "Category activated" : "Category deactivated");
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Category status update failed");
    } finally {
      setSaving(false);
    }
  }

  async function toggleSubcategoryStatus(sub) {
    const id = normalizeId(sub);
    if (!id) return;
    const next = !isActiveOf(sub);

    try {
      setSaving(true);
      setError("");
      await restaurantAdminService.setFoodSubcategoryStatus(id, next);
      await afterSaved(next ? "Subcategory activated" : "Subcategory deactivated");
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Subcategory status update failed");
    } finally {
      setSaving(false);
    }
  }

  async function toggleTagStatus(tag) {
    const id = normalizeId(tag);
    if (!id) return;
    const next = !isActiveOf(tag);

    try {
      setSaving(true);
      setError("");
      await restaurantAdminService.setFoodTagStatus(id, next);
      await afterSaved(next ? "Tag activated" : "Tag deactivated");
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Tag status update failed");
    } finally {
      setSaving(false);
    }
  }

  async function deleteCategory(cat) {
    const id = normalizeId(cat);
    if (!id) return;

    const ok = await appConfirm(`Delete category "${normalizeName(cat)}"? Food items under it can stop showing correctly.`, {
      title: "Delete Category",
      danger: true,
      confirmText: "DELETE",
    });
    if (!ok) return;

    try {
      setSaving(true);
      setError("");
      await restaurantAdminService.deleteFoodCategory(id);
      if (String(selectedCategoryId) === String(id)) setSelectedCategoryId("");
      if (String(editingMainId) === String(id)) {
        clearNodeEdit();
        setNodeForm({ ...emptyNodeForm, type: "main" });
      }
      await afterSaved("Category deleted");
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Category delete failed");
    } finally {
      setSaving(false);
    }
  }

  async function deleteSubcategory(sub) {
    const id = normalizeId(sub);
    if (!id) return;

    const ok = await appConfirm(`Delete subcategory "${normalizeName(sub)}"? Food items under it can stop showing correctly.`, {
      title: "Delete Subcategory",
      danger: true,
      confirmText: "DELETE",
    });
    if (!ok) return;

    try {
      setSaving(true);
      setError("");
      await restaurantAdminService.deleteFoodSubcategory(id);
      if (String(editingSubId) === String(id)) {
        clearNodeEdit();
        setNodeForm({ ...emptyNodeForm, type: "sub", parentId: selectedCategoryId || "" });
      }
      await afterSaved("Subcategory deleted");
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Subcategory delete failed");
    } finally {
      setSaving(false);
    }
  }

  async function saveTag() {
    const name = tagForm.name.trim();
    if (!name) return setError("Tag name is required");

    try {
      setSaving(true);
      setError("");
      await restaurantAdminService.createFoodTag({ name, tagType: tagForm.tagType || "cuisine", isActive: true });
      setTagForm({ name: "", tagType: "cuisine" });
      await afterSaved("Tag added");
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Tag save failed");
    } finally {
      setSaving(false);
    }
  }

  if (!open) return null;

  const isEditing = Boolean(editingMainId || editingSubId);
  const isSubMode = nodeForm.type === "sub";

  return (
    <div className="fixed inset-0 z-[9999] bg-black/35">
      <div className="absolute right-0 top-0 flex h-full w-[980px] max-w-[96vw] flex-col bg-white shadow-2xl">
        <div className="flex items-center justify-between gap-4 border-b border-[#f4d9dd] px-5 py-5">
          <div>
            <h2 className="text-[22px] font-semibold text-slate-900">Manage Catalog</h2>
            <p className="mt-1 text-[12px] text-slate-500">Manage categories, subcategories and tags used across the catalog.</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-2 text-slate-500 hover:bg-[#fff0f2] hover:text-[#d86a78]">
            <X size={24} />
          </button>
        </div>

        <div className="border-b border-[#f4d9dd] px-5 py-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              {[
                ["categories", "Categories"],
                ["tags", "Tags"],
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    setTab(key);
                    setError("");
                    setSuccess("");
                  }}
                  className={`h-[42px] rounded-[10px] px-5 text-[13px] font-semibold transition ${
                    tab === key ? "bg-[#d86a78] text-white" : "border border-[#f2c6cc] bg-white text-[#d86a78] hover:bg-[#fff6f7]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="relative w-full lg:max-w-[320px]">
              <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-300" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search catalog" className="pl-10" />
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          {error ? <div className="mb-4 rounded-[14px] border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] font-bold text-rose-600">{error}</div> : null}
          {success ? (
            <div className="mb-4 flex items-center gap-2 rounded-[14px] border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] font-bold text-emerald-600">
              <CheckCircle2 size={16} /> {success}
            </div>
          ) : null}

          {loading ? <div className="py-14 text-center text-[13px] font-bold text-slate-400">Loading catalog...</div> : null}

          {!loading && tab === "categories" ? (
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-[340px_1fr]">
              <div className="rounded-[18px] border border-[#f2c6cc] bg-[#fffafb] p-4">
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-[16px] font-semibold text-slate-950">Main Categories</h3>
                    <p className="text-[12px] text-slate-500">Select any category to manage its subcategories.</p>
                  </div>
                  <Button onClick={startAddMainCategory}>
                    <Plus size={14} /> Main
                  </Button>
                </div>

                <div className="max-h-[calc(100vh-260px)] space-y-3 overflow-y-auto pr-1">
                  {filteredCategories.length === 0 ? <div className="py-10 text-center text-[13px] font-bold text-slate-400">No categories found</div> : null}

                  {filteredCategories.map((cat) => {
                    const id = normalizeId(cat);
                    const active = isActiveOf(cat);
                    const selected = String(id) === String(selectedCategoryId);
                    const subCount = subcategories.filter((sub) => String(categoryIdOf(sub)) === String(id)).length;

                    return (
                      <div
                        key={id}
                        className={`rounded-[16px] border p-3 transition ${selected ? "border-[#d86a78] bg-white shadow-sm" : "border-[#f2c6cc] bg-white hover:bg-[#fff6f7]"}`}
                      >
                        <div className="flex items-center gap-3">
                          <button type="button" onClick={() => setSelectedCategoryId(id)} className="flex min-w-0 flex-1 items-center gap-3 text-left">
                            <div className="flex h-[54px] w-[54px] shrink-0 items-center justify-center overflow-hidden rounded-[12px] border border-[#f2c6cc] bg-white">
                              <EntityImage item={cat} label={normalizeName(cat)} />
                            </div>

                            <div className="min-w-0 flex-1">
                              <div className="truncate text-[14px] font-semibold text-slate-900">{normalizeName(cat)}</div>
                              <div className="mt-1 flex flex-wrap items-center gap-2">
                                <span className="text-[11px] text-slate-500">{subCount} subcategories</span>
                                <ActiveBadge active={active} />
                              </div>
                            </div>
                          </button>

                          <div className="ml-auto flex shrink-0 items-center justify-end gap-1.5">
                            <IconButton title="Edit category" onClick={() => startEditCategory(cat)}>
                              <Pencil size={13} />
                            </IconButton>
                            <IconButton
                              title={active ? "Deactivate category" : "Activate category"}
                              disabled={saving}
                              onClick={() => toggleCategoryStatus(cat)}
                              className={active ? "" : "border-emerald-200 text-emerald-600 hover:bg-emerald-50"}
                            >
                              <Power size={13} />
                            </IconButton>
                            <IconButton title="Delete category" danger disabled={saving} onClick={() => deleteCategory(cat)}>
                              <Trash2 size={13} />
                            </IconButton>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="rounded-[18px] border border-[#f2c6cc] bg-[#fffafb] p-4">
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-[16px] font-semibold text-slate-950">
                      {selectedCategory ? `${normalizeName(selectedCategory)} Subcategories` : "Subcategories"}
                    </h3>
                    <p className="text-[12px] text-slate-500">Add, edit, delete and manage images from here.</p>
                  </div>

                  <Button onClick={startAddSubcategory} disabled={!selectedCategoryId}>
                    <Plus size={14} /> Subcategory
                  </Button>
                </div>

                <div className="mb-4 rounded-[16px] border border-[#f2c6cc] bg-white p-4">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div>
                      <div className="text-[14px] font-semibold text-slate-950">
                        {isEditing ? `Edit ${isSubMode ? "Subcategory" : "Category"}` : `Add ${isSubMode ? "Subcategory" : "Category"}`}
                      </div>
                      <div className="text-[12px] text-slate-500">This form is wired with food catalog APIs.</div>
                    </div>
                    {isEditing ? <Button variant="ghost" onClick={() => { clearNodeEdit(); setNodeForm({ ...emptyNodeForm, type: isSubMode ? "sub" : "main", parentId: selectedCategoryId || "" }); }}>Cancel Edit</Button> : null}
                  </div>

                  <div className="space-y-3">
                    <Input
                      placeholder={isSubMode ? "Enter subcategory name" : "Enter category name"}
                      value={nodeForm.name}
                      onChange={(e) => setNodeForm((prev) => ({ ...prev, name: e.target.value }))}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          saveNode();
                        }
                      }}
                    />

                    <div>
                      <div className="mb-2 text-[12px] font-semibold text-slate-600">Type</div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => setNodeType("main")}
                          className={`h-[38px] rounded-[10px] px-4 text-[12px] font-semibold transition ${
                            !isSubMode ? "bg-[#d86a78] text-white" : "border border-[#f2c6cc] bg-white text-[#d86a78] hover:bg-[#fff6f7]"
                          }`}
                        >
                          Main Category
                        </button>
                        <button
                          type="button"
                          onClick={() => setNodeType("sub")}
                          className={`h-[38px] rounded-[10px] px-4 text-[12px] font-semibold transition ${
                            isSubMode ? "bg-[#d86a78] text-white" : "border border-[#f2c6cc] bg-white text-[#d86a78] hover:bg-[#fff6f7]"
                          }`}
                        >
                          Subcategory
                        </button>
                      </div>
                    </div>

                    {isSubMode ? (
                      <div>
                        <div className="mb-2 text-[12px] font-semibold text-slate-600">Parent Category</div>
                        <SelectLike
                          value={nodeForm.parentId || selectedCategoryId || ""}
                          onChange={(value) => {
                            setNodeForm((prev) => ({ ...prev, parentId: value }));
                            if (value) setSelectedCategoryId(value);
                          }}
                          placeholder="Select parent category"
                          options={categoryOptions}
                        />
                        <div className="mt-2 text-[11px] text-slate-500">This subcategory will appear under the selected main category.</div>
                      </div>
                    ) : (
                      <div className="text-[11px] text-slate-500">Main category will appear as a top-level category in the app.</div>
                    )}

                    <SingleImageUpload
                      label={isSubMode ? "Subcategory Image" : "Category Image"}
                      required
                      value={nodeForm.imageUrl}
                      onChange={(imageUrl) => setNodeForm((prev) => ({ ...prev, imageUrl }))}
                      onError={setError}
                    />

                    <Button className="w-full" disabled={saving || !nodeForm.name.trim()} onClick={saveNode}>
                      {saving ? "Saving..." : isEditing ? `Update ${isSubMode ? "Subcategory" : "Category"}` : `Add ${isSubMode ? "Subcategory" : "Category"}`}
                    </Button>
                  </div>
                </div>

                <div className="rounded-[16px] border border-dashed border-[#f2c6cc] bg-white p-4">
                  {!selectedCategoryId ? <div className="py-8 text-center text-[13px] text-slate-500">Select a main category first</div> : null}
                  {selectedCategoryId && selectedSubcategories.length === 0 ? <div className="py-8 text-center text-[13px] text-slate-500">No subcategories found for this category</div> : null}

                  {selectedSubcategories.map((sub) => {
                    const active = isActiveOf(sub);
                    return (
                      <div key={normalizeId(sub)} className="mb-2 flex flex-col gap-3 rounded-[14px] border border-[#f2c6cc] px-4 py-3 text-[13px] font-medium text-slate-700 md:flex-row md:items-center md:justify-between">
                        <div className="flex min-w-0 items-center gap-3">
                          <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-[#f2c6cc] bg-white">
                            <EntityImage item={sub} label={normalizeName(sub)} />
                          </div>
                          <div className="min-w-0">
                            <div className="truncate font-semibold text-slate-900">{normalizeName(sub)}</div>
                            <div className="mt-1 flex flex-wrap items-center gap-2">
                              <span className="text-[11px] text-slate-500">{selectedCategory ? normalizeName(selectedCategory) : sub.categoryName || sub.category_name || "-"}</span>
                              <ActiveBadge active={active} />
                            </div>
                          </div>
                        </div>

                        <div className="ml-auto flex shrink-0 items-center justify-end gap-1.5">
                          <IconButton title="Edit subcategory" onClick={() => startEditSubcategory(sub)}>
                            <Pencil size={13} />
                          </IconButton>
                          <IconButton
                            title={active ? "Deactivate subcategory" : "Activate subcategory"}
                            disabled={saving}
                            onClick={() => toggleSubcategoryStatus(sub)}
                            className={active ? "" : "border-emerald-200 text-emerald-600 hover:bg-emerald-50"}
                          >
                            <Power size={13} />
                          </IconButton>
                          <IconButton title="Delete subcategory" danger disabled={saving} onClick={() => deleteSubcategory(sub)}>
                            <Trash2 size={13} />
                          </IconButton>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : null}

          {!loading && tab === "tags" ? (
            <div className="rounded-[18px] border border-[#f2c6cc] bg-[#fffafb] p-4">
              <div className="mb-4">
                <h3 className="text-[16px] font-semibold text-slate-950">Food Tags</h3>
                <p className="text-[12px] text-slate-500">Cuisine, region and diet tags like North Indian, Fast Food, Jain or South Indian.</p>
              </div>

              <div className="mb-4 grid grid-cols-1 gap-3 rounded-[16px] border border-[#f2c6cc] bg-white p-4 md:grid-cols-[1fr_180px_auto]">
                <Input placeholder="Tag name" value={tagForm.name} onChange={(e) => setTagForm((prev) => ({ ...prev, name: e.target.value }))} />
                <SelectLike
                  value={tagForm.tagType}
                  onChange={(value) => setTagForm((prev) => ({ ...prev, tagType: value || "cuisine" }))}
                  placeholder="Tag type"
                  options={[
                    { value: "cuisine", label: "Cuisine" },
                    { value: "region", label: "Region" },
                    { value: "diet", label: "Diet" },
                    { value: "custom", label: "Custom" },
                  ]}
                />
                <Button disabled={saving || !tagForm.name.trim()} onClick={saveTag}>
                  {saving ? "Saving..." : "Add Tag"}
                </Button>
              </div>

              <div className="flex flex-wrap gap-2 rounded-[16px] border border-dashed border-[#f2c6cc] bg-white p-4">
                {visibleTags.length === 0 ? <div className="py-8 text-center text-[13px] text-slate-500">No tags found</div> : null}

                {visibleTags.map((tag) => {
                  const active = isActiveOf(tag);
                  return (
                    <span key={normalizeId(tag)} className="inline-flex items-center gap-2 rounded-full border border-[#f2c6cc] bg-white px-3 py-1.5 text-[12px] font-bold text-slate-600">
                      {normalizeName(tag)}
                      <span className="text-[10px] font-semibold uppercase text-slate-400">{tag.tagType || tag.tag_type || "tag"}</span>
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => toggleTagStatus(tag)}
                        className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${active ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}
                      >
                        {active ? "Active" : "Inactive"}
                      </button>
                    </span>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
